import { PDFDocument } from 'pdf-lib'

export interface CroppedPageData {
  id: string
  imageData: string
  regionId: string
}

export function usePdfEditor() {
  /**
   * Replace a specific page in a PDF with multiple cropped images
   * @param pdfBytes - Original PDF as Uint8Array
   * @param pageIndex - Index of the page to replace (0-based)
   * @param croppedPages - Array of cropped page data
   * @returns Modified PDF as Uint8Array
   */
  async function replacePdfPageWithCroppedImages(
    pdfBytes: Uint8Array,
    pageIndex: number,
    croppedPages: CroppedPageData[]
  ): Promise<Uint8Array> {
    try {
      // Check if PDF-lib is available
      if (!PDFDocument) {
        throw new Error('PDF-lib is not available. Please refresh the page and try again.')
      }

      // Load the original PDF
      const pdfDoc = await PDFDocument.load(pdfBytes)

      // Remove the original page
      pdfDoc.removePage(pageIndex)

      // Insert cropped pages at the same position
      for (let i = 0; i < croppedPages.length; i++) {
        const croppedPage = croppedPages[i]

        // Convert base64 image data to bytes
        const imageBytes = base64ToUint8Array(croppedPage.imageData)

        // Embed the image (assuming PNG format)
        const image = await pdfDoc.embedPng(imageBytes)

        // Get image dimensions
        const { width: imgWidth, height: imgHeight } = image.scale(1)

        // Create a new page with the image dimensions
        const page = pdfDoc.insertPage(pageIndex + i, [imgWidth, imgHeight])

        // Draw the image to fill the entire page
        page.drawImage(image, {
          x: 0,
          y: 0,
          width: imgWidth,
          height: imgHeight
        })
      }

      // Save and return the modified PDF
      const modifiedPdfBytes = await pdfDoc.save()
      console.log(
        `PDF page replacement: ${croppedPages.length} pages inserted, final size: ${modifiedPdfBytes.length} bytes`
      )
      return modifiedPdfBytes
    } catch (error) {
      console.error('Error replacing PDF page:', error)
      throw new Error('Failed to replace PDF page with cropped images')
    }
  }

  /**
   * Convert base64 data URL to Uint8Array
   * @param base64DataUrl - Base64 data URL (e.g., "data:image/png;base64,...")
   * @returns Uint8Array of the image data
   */
  function base64ToUint8Array(base64DataUrl: string): Uint8Array {
    // Remove the data URL prefix (e.g., "data:image/png;base64,")
    const base64Data = base64DataUrl.split(',')[1]

    // Convert base64 to binary string
    const binaryString = atob(base64Data)

    // Convert binary string to Uint8Array
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }

    return bytes
  }

  /**
   * Download a PDF file
   * @param pdfBytes - PDF data as Uint8Array
   * @param filename - Name for the downloaded file
   */
  function downloadPdf(pdfBytes: Uint8Array, filename: string) {
    const blob = new Blob([pdfBytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the object URL
    URL.revokeObjectURL(url)
  }

  /**
   * Get PDF as blob for further processing
   * @param pdfBytes - PDF data as Uint8Array
   * @returns Blob containing the PDF data
   */
  function getPdfBlob(pdfBytes: Uint8Array): Blob {
    return new Blob([pdfBytes], { type: 'application/pdf' })
  }

  return {
    replacePdfPageWithCroppedImages,
    downloadPdf,
    getPdfBlob,
    base64ToUint8Array
  }
}
